import type React from 'react';
import {useMemo, useState} from 'react';
import {generatePath, useNavigate, useParams, useSearchParams} from 'react-router';
import {Alert, Box, MenuItem, Skeleton, Stack, TextField, Typography} from '@mui/material';
import {CustomDataGrid} from '~/components/data-grid/CustomDataGrid';
import {DataGridCsvExportButton} from '~/components/data-grid/DataGridCsvExportButton';
import {SegmentedControl} from '~/components/segmented-control/SegmentedControl';
import {routes} from '~/routing/routes';
import {expansionDepth, exploreRows, levelsBelow, type LevelFilter, matchingCount, type RiskFilter, validPath} from '../domain/exploreTree';
import {useExploreQuery} from '../hooks/useCategoryProfileQueries';
import {useMoneyFormat} from '../hooks/useMoneyFormat';
import {CategoryNavigator} from './CategoryNavigator';
import {SettingsLink} from '../settings/SettingsLink';
import {ExploreBreadcrumb} from './ExploreBreadcrumb';
import {ExploreKpis} from './ExploreKpis';
import {CategoryTreeCell} from './CategoryTreeCell';
import {getExploreColumns, RISK_LABEL} from './exploreColumns';

const NAV_COLLAPSED_KEY = 'ec-nav-collapsed';

function readCollapsed(): boolean {
    try {
        return localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
    } catch {
        return false;
    }
}

function writeCollapsed(value: boolean) {
    try {
        localStorage.setItem(NAV_COLLAPSED_KEY, value ? '1' : '0');
    } catch {
        // Storage can be unavailable (private windows); the panel still works for this visit.
    }
}

// Depth tints (spec 3.6 "outline tree with depth tints"), stronger than the grid's own group/leaf row colours.
const DEPTH_TINTS = {
    '&& .MuiDataGrid-row.cp-depth-0': {bgcolor: '#E3EAF5', '& .MuiDataGrid-cell': {fontWeight: 600}},
    '&& .MuiDataGrid-row.cp-depth-1': {bgcolor: '#EEF2F9'},
    '&& .MuiDataGrid-row.cp-depth-2': {bgcolor: '#F7F9FC'},
    '&& .MuiDataGrid-row.cp-depth-3': {bgcolor: '#FFFFFF'},
    '&& .MuiDataGrid-row:hover': {filter: 'brightness(0.97)'},
    // Phones: the breadcrumb takes the toolbar's first line, the toolbar buttons the next.
    '& .MuiDataGrid-toolbar': {flexWrap: {xs: 'wrap', sm: 'nowrap'}, height: 'auto', minHeight: 44},
    '& .MuiDataGrid-toolbar > .MuiStack-root:first-of-type': {flexBasis: {xs: '100%', sm: 'auto'}},
};

/** Explore Categories (spec 3): where the money goes, from L1 down to the leaves. */
export const ExploreCategoriesPage: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const databagId = Number(id);
    const navigate = useNavigate();
    const money = useMoneyFormat();
    const [searchParams, setSearchParams] = useSearchParams();
    // 'all' (the default) shows the tree fully expanded; a level shows it expanded down to that level.
    const [level, setLevel] = useState<LevelFilter>('all');
    const [risk, setRisk] = useState<RiskFilter>('all');
    const [collapsed, setCollapsed] = useState(readCollapsed);
    const {data, isLoading, isError, error} = useExploreQuery(databagId);

    const selected = data ? validPath(data.tree, searchParams.get('path') ?? '') : '';
    const rows = useMemo(() => data ? exploreRows(data.tree, selected, 'all', risk) : [], [data, selected, risk]);
    const levels = data ? levelsBelow(data.tree, selected) : [];

    const select = (path: string) => {
        setLevel('all');
        setSearchParams(path ? {path} : {}, {replace: true});
    };
    const openProfile = (path: string) => {
        const target = generatePath(routes.category_profile, {id: String(databagId), tab: 'overview'});
        navigate(`${target}?${new URLSearchParams({path, from: selected})}`);
    };
    const columns = useMemo(
        () => getExploreColumns({money, onViewDetails: r => openProfile(r.path), withNameColumn: false}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [money, selected, databagId]);

    if (isError) {
        const status = (error as {response?: {status?: number}})?.response?.status;
        return <Alert severity={status === 403 ? 'warning' : 'error'}>
            {status === 403 ? 'You do not have access to this dataset.' : 'Could not load the categories.'}
        </Alert>;
    }
    if (isLoading || !data) {
        return <Stack gap={2}><Skeleton height={48}/><Skeleton variant="rounded" height={400}/></Stack>;
    }
    if (!data.kpis) {
        return (
            <Alert severity="info" sx={{m: 2}}>
                <b>No categorized spend yet.</b> Category figures appear after the dataset is categorized and its
                category facts are built. Ask Mithra to run the build if this dataset was just delivered.
            </Alert>
        );
    }

    const levelLabels: LevelFilter[] = ['all', ...levels];
    const levelValue = Math.max(0, levelLabels.indexOf(level));

    return (
        <Stack direction="row" gap={collapsed ? 1 : 2} sx={{height: '100%', minHeight: 0}}>
            <CategoryNavigator
                data={data} selected={selected} onSelect={select} money={money} collapsed={collapsed}
                onToggleCollapsed={() => {
                    writeCollapsed(!collapsed);
                    setCollapsed(!collapsed);
                }}
            />
            <Stack gap={2} sx={{flex: 1, minWidth: 0}}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="h5" component="h1" sx={{fontWeight: 600}}>Explore Categories</Typography>
                    <SettingsLink databagId={databagId}/>
                </Stack>
                <ExploreKpis data={data} selected={selected} money={money}/>
                {(data.kpis.uncategorizedSharePct ?? 0) >= 20 && (
                    <Alert severity="warning">
                        {`${data.kpis.uncategorizedSharePct?.toFixed(0)}% of spend is uncategorized and is not in this tree.`}
                    </Alert>
                )}
                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                    {levelLabels.length <= 6 ? (
                        <SegmentedControl
                            labels={levelLabels.map(l => l === 'all' ? 'All categories' : `L${l}`)}
                            value={levelValue}
                            onChange={i => setLevel(levelLabels[i])}
                        />
                    ) : (
                        <TextField select size="small" label="Level" value={String(level)} sx={{minWidth: 160}}
                                   onChange={e => setLevel(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
                            {levelLabels.map(l => <MenuItem key={l} value={String(l)}>{l === 'all' ? 'All categories' : `L${l}`}</MenuItem>)}
                        </TextField>
                    )}
                    <TextField select size="small" label="Risk" value={risk} sx={{minWidth: 160}}
                               onChange={e => setRisk(e.target.value as RiskFilter)}>
                        <MenuItem value="all">All risks</MenuItem>
                        {(['high', 'medium', 'low'] as const).map(r => <MenuItem key={r} value={r}>{RISK_LABEL[r]}</MenuItem>)}
                    </TextField>
                </Stack>
                <Box sx={{flex: 1, minHeight: 300}}>
                    <CustomDataGrid
                        layoutKey="explore-categories"
                        rows={rows}
                        columns={columns}
                        getRowId={r => r.path}
                        key={`${selected}:${String(level)}`} // remount so a new level filter re-applies the expansion depth
                        treeData
                        getTreeDataPath={r => r.treePath}
                        defaultGroupingExpansionDepth={expansionDepth(level, levels)}
                        groupingColDef={{
                            headerName: 'Category', width: 300,
                            renderCell: params => <CategoryTreeCell {...params} onOpen={r => openProfile(r.path)}/>,
                        }}
                        pagination={false}
                        hideFooter
                        disableRowSelectionOnClick
                        getRowClassName={p => `cp-depth-${Math.min(p.row.treePath.length - 1, 3)}`
                            + (p.row.riskContext ? ' cp-risk-context' : '')}
                        sx={{...DEPTH_TINTS, '& .cp-risk-context': {opacity: 0.55}}}
                        showToolbar
                        toolbarProps={{
                            children: (
                                <>
                                    <ExploreBreadcrumb selected={selected} count={matchingCount(rows)} onSelect={select}/>
                                    <DataGridCsvExportButton fileName="explore-categories"/>
                                </>
                            ),
                        }}
                        localeText={{
                            noRowsLabel: risk !== 'all'
                                ? `No ${RISK_LABEL[risk].toLowerCase()} categories under ${selected || 'all categories'}.`
                                : `Nothing sits under ${selected || 'all categories'}.`,
                        }}
                    />
                </Box>
            </Stack>
        </Stack>
    );
};
